'use client';

import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle2, Copy, Globe, Mail, RefreshCw, Trash2 } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useToast } from '@/components/ui/use-toast';
import { trpc } from '@/lib/api/trpc';
import { usePartnerAdmin } from '@/hooks/use-partner-admin';

const REGIONS = [
  { value: 'us-east-1', label: 'US East (N. Virginia)' },
  { value: 'eu-west-1', label: 'EU West (Ireland)' },
  { value: 'sa-east-1', label: 'South America (São Paulo)' },
  { value: 'ap-northeast-1', label: 'Asia Pacific (Tokyo)' },
] as const;

type Region = (typeof REGIONS)[number]['value'];

const DOMAIN_REGEX = /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;

interface DnsRow {
  record: string;
  name: string;
  type: string;
  value: string;
  ttl?: string;
  status?: string;
  priority?: number;
  manual?: boolean;
}

const DMARC_ROW: DnsRow = {
  record: 'DMARC',
  name: '_dmarc',
  type: 'TXT',
  value: 'v=DMARC1; p=none;',
  ttl: 'Auto',
  manual: true,
};

const STATUS_STYLE: Record<string, string> = {
  verified: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300',
  pending: 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300',
  not_started: 'bg-muted text-muted-foreground',
  failed: 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300',
  temporary_failure: 'bg-orange-100 text-orange-700 dark:bg-orange-950 dark:text-orange-300',
};

function StatusBadge({ status }: { status: string }) {
  return (
    <span
      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize ${
        STATUS_STYLE[status] ?? 'bg-muted text-muted-foreground'
      }`}
    >
      {status.replace('_', ' ')}
    </span>
  );
}

function DnsRecordsCard({
  records,
  onVerify,
  isVerifying,
}: {
  records: DnsRow[];
  onVerify: () => void;
  isVerifying: boolean;
}) {
  const { toast } = useToast();
  const rows = [...records, DMARC_ROW];
  const showPriority = rows.some((r) => r.type === 'MX');

  const copy = async (value: string) => {
    await navigator.clipboard.writeText(value);
    toast({ title: 'Copied', description: 'Value copied to clipboard' });
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <CardTitle className="text-lg">DNS records</CardTitle>
            <CardDescription>
              Add these records at your DNS provider, then hit Verify. Propagation can take up to
              24h. Click a value to copy it.
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={onVerify} disabled={isVerifying}>
            <RefreshCw className={`mr-2 h-3.5 w-3.5 ${isVerifying ? 'animate-spin' : ''}`} />
            Verify domain
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Record</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Value</TableHead>
                <TableHead>TTL</TableHead>
                {showPriority && <TableHead>Priority</TableHead>}
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r, i) => (
                <TableRow key={`${r.record}-${i}`} className={r.manual ? 'bg-muted/40' : ''}>
                  <TableCell className="font-mono text-xs">{r.record}</TableCell>
                  <TableCell className="font-mono text-xs">{r.type}</TableCell>
                  <TableCell className="font-mono text-xs">{r.name}</TableCell>
                  <TableCell className="max-w-md">
                    <button
                      type="button"
                      onClick={() => copy(r.value)}
                      title={r.value}
                      className="hover:bg-muted inline-flex max-w-full items-center gap-1.5 truncate rounded px-1.5 py-0.5 text-left font-mono text-xs"
                    >
                      <span className="truncate">{r.value}</span>
                      <Copy className="text-muted-foreground h-3 w-3 shrink-0" />
                    </button>
                  </TableCell>
                  <TableCell className="font-mono text-xs">{r.ttl || 'Auto'}</TableCell>
                  {showPriority && (
                    <TableCell className="font-mono text-xs">
                      {r.type === 'MX' ? r.priority : '—'}
                    </TableCell>
                  )}
                  <TableCell>
                    {r.manual ? (
                      <Badge variant="secondary" className="text-xs">
                        recommended
                      </Badge>
                    ) : (
                      <StatusBadge status={r.status ?? 'not_started'} />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

function AddDomainCard({ partner }: { partner: string }) {
  const { toast } = useToast();
  const utils = trpc.useUtils();
  const [domain, setDomain] = useState('');
  const [fromLocal, setFromLocal] = useState('notifications');
  const [region, setRegion] = useState<Region>('us-east-1');

  const addMutation = trpc.partnerEmailDomain.add.useMutation({
    onSuccess: () => {
      utils.partnerEmailDomain.get.invalidate();
      toast({ title: 'Domain registered', description: 'Now add the DNS records below.' });
    },
    onError: (e) => toast({ title: 'Failed to add domain', description: e.message, variant: 'destructive' }),
  });

  const trimmed = domain.trim().toLowerCase();
  const isValid = DOMAIN_REGEX.test(trimmed) && /^[a-zA-Z0-9._-]+$/.test(fromLocal.trim());

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Globe className="h-4 w-4" />
          Set up your sending domain
        </CardTitle>
        <CardDescription>
          Use a dedicated subdomain (e.g. <span className="font-mono">send.yourbrand.com</span>) so
          its email reputation stays independent from your main website. Emails to your merchants
          (sign-in codes, invoices, statements, invites) will be sent from this domain once
          verified.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="space-y-1.5">
            <Label htmlFor="domain">Sending domain</Label>
            <Input
              id="domain"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder="send.yourbrand.com"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="fromLocal">From address</Label>
            <div className="flex items-center gap-1">
              <Input
                id="fromLocal"
                value={fromLocal}
                onChange={(e) => setFromLocal(e.target.value)}
                className="max-w-[160px]"
              />
              <span className="text-muted-foreground whitespace-nowrap text-sm">
                @{trimmed || 'send.yourbrand.com'}
              </span>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Region</Label>
            <Select value={region} onValueChange={(v) => setRegion(v as Region)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {REGIONS.map((r) => (
                  <SelectItem key={r.value} value={r.value}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <Button
          onClick={() =>
            addMutation.mutate({ partner, domain: trimmed, fromLocal: fromLocal.trim(), region })
          }
          disabled={!isValid || addMutation.isPending}
        >
          {addMutation.isPending ? 'Registering…' : 'Add domain'}
        </Button>
      </CardContent>
    </Card>
  );
}

function CurrentDomainCard({
  partner,
  config,
  onVerify,
  isVerifying,
}: {
  partner: string;
  config: { domain: string; status: string; fromLocal: string; region?: string; verifiedAt?: string | null };
  onVerify: () => void;
  isVerifying: boolean;
}) {
  const { toast } = useToast();
  const utils = trpc.useUtils();
  const removeMutation = trpc.partnerEmailDomain.remove.useMutation({
    onSuccess: () => {
      utils.partnerEmailDomain.get.invalidate();
      toast({ title: 'Domain removed', description: 'Emails fall back to the platform domain.' });
    },
    onError: (e) => toast({ title: 'Failed to remove domain', description: e.message, variant: 'destructive' }),
  });

  const handleRemove = () => {
    if (!confirm(`Remove ${config.domain}? Emails will fall back to the platform sending domain.`))
      return;
    removeMutation.mutate({ partner });
  };

  return (
    <Card className={config.status === 'verified' ? 'border-emerald-200 dark:border-emerald-900' : 'border-amber-200 dark:border-amber-900'}>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Mail className="h-4 w-4" />
            {config.domain}
            <StatusBadge status={config.status} />
          </CardTitle>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onVerify} disabled={isVerifying}>
              <RefreshCw className={`mr-2 h-3.5 w-3.5 ${isVerifying ? 'animate-spin' : ''}`} />
              Refresh status
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={handleRemove}
              disabled={removeMutation.isPending || isVerifying}
            >
              <Trash2 className="mr-2 h-3.5 w-3.5" />
              Remove
            </Button>
          </div>
        </div>
        <CardDescription>
          {config.status === 'verified' ? (
            <span className="flex items-center gap-1.5 text-emerald-600">
              <CheckCircle2 className="h-3.5 w-3.5" />
              Verified — your emails are sent from{' '}
              <span className="font-mono">
                {config.fromLocal}@{config.domain}
              </span>
            </span>
          ) : (
            'Waiting for DNS verification. Until then, emails keep using the platform domain.'
          )}
        </CardDescription>
      </CardHeader>
    </Card>
  );
}

export default function PartnerAdminEmailDomainPage() {
  const { partner: partnerParam } = useParams<{ partner: string }>();
  const { partner, canView } = usePartnerAdmin(partnerParam);
  const { toast } = useToast();
  const utils = trpc.useUtils();

  const { data, isLoading, error } = trpc.partnerEmailDomain.get.useQuery(
    { partner: partner ?? undefined },
    { enabled: canView && !!partner, refetchOnWindowFocus: false },
  );

  const verifyMutation = trpc.partnerEmailDomain.verify.useMutation({
    onSuccess: (res) => {
      utils.partnerEmailDomain.get.invalidate();
      toast(
        res.config.status === 'verified'
          ? { title: 'Domain verified', description: 'Your emails now use your own domain.' }
          : { title: 'Not verified yet', description: 'DNS records still propagating — retry later.' },
      );
    },
    onError: (e) => toast({ title: 'Verification failed', description: e.message, variant: 'destructive' }),
  });

  if (!canView || !partner) return null;

  if (error) {
    return (
      <Card className="border-destructive">
        <CardContent className="py-8 text-center">
          <p className="text-destructive font-medium">Failed to load email domain settings</p>
          <p className="text-muted-foreground mt-1 text-sm">{error.message}</p>
        </CardContent>
      </Card>
    );
  }

  const config = data?.config ?? null;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Sending email domain</h2>
        <p className="text-muted-foreground text-sm">
          White-label the emails your merchants receive: verify your own domain and every
          notification (sign-in codes, welcome emails, invoices, statements, staff invites) is sent
          from it instead of the platform domain.
        </p>
      </div>

      {isLoading ? (
        <div className="space-y-4">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      ) : !config ? (
        <AddDomainCard partner={partner} />
      ) : (
        <>
          <CurrentDomainCard
            partner={partner}
            config={config}
            onVerify={() => verifyMutation.mutate({ partner })}
            isVerifying={verifyMutation.isPending}
          />
          <DnsRecordsCard
            records={config.dnsRecords ?? []}
            onVerify={() => verifyMutation.mutate({ partner })}
            isVerifying={verifyMutation.isPending}
          />
        </>
      )}
    </div>
  );
}
